import React, { useCallback, useEffect, useState } from 'react'
import {
  Loader2, Plus, DollarSign, Trash2, CalendarX, Eye, EyeOff, Radio, Globe, X
} from 'lucide-react'
import { Tooltip } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, {
  RateCard, BroadcastChannel
} from '../../services/broadcastAdminService'

interface Props { customerId: string }

const field = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg '
  + 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white'
const label = 'block text-xs font-medium text-gray-600 mb-1'

const money = (n: number, ccy: string) =>
  Number(n).toLocaleString(undefined, {
    style: 'currency', currency: ccy || 'USD', minimumFractionDigits: 2
  })

/** Date-only columns: parse and print as UTC or they shift a day westward. */
const day = (d: string | null) => d
  ? new Date(`${String(d).slice(0, 10)}T00:00:00Z`)
      .toLocaleDateString(undefined, { dateStyle: 'medium', timeZone: 'UTC' })
  : null

/**
 * Rate cards — the named CPMs a customer's broadcasts are assigned to.
 *
 * TWO RULES SHAPE THIS SCREEN, and they are easy to conflate:
 *
 *  1. A RATE IS NEVER EDITED. A broadcast stores rate_card_id, so a card's
 *     CPM *is* the price of everything assigned to it; editing would restate
 *     broadcasts already shown to a sponsor. So there is no edit control on
 *     CPM anywhere here — repricing is a new card.
 *
 *  2. CLOSING AND DISABLING ARE DIFFERENT THINGS.
 *       close   — records when the rate stopped applying. Changes nothing,
 *                 hides nothing. A closed card is still the right one to
 *                 assign to a game that aired while it was in force.
 *       disable — stops offering it in the picker. Broadcasts already
 *                 assigned keep their price.
 *     Which is why the list groups by OFFERED, not by closed: "what can I
 *     pick" is the question, and a closed-but-current card is still pickable.
 */
const BroadcastRateCards: React.FC<Props> = ({ customerId }) => {
  const { showSuccess, showError } = useToast()
  const [cards, setCards] = useState<RateCard[]>([])
  const [channels, setChannels] = useState<BroadcastChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const [closing, setClosing] = useState<RateCard | null>(null)

  const [name, setName] = useState('')
  const [cpm, setCpm] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [channelId, setChannelId] = useState('')
  const [from, setFrom] = useState('')
  const [notes, setNotes] = useState('')
  const [closeDate, setCloseDate] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Everything, disabled included: this is the management screen. The
      // picker is the one that asks for enabledOnly.
      const [c, ch] = await Promise.all([
        broadcastAdminService.listRateCards(customerId),
        broadcastAdminService.listChannels(customerId)
      ])
      setCards(c.data || [])
      setChannels(ch.data?.all || [])
    } catch (err) {
      showError('Failed to load', err instanceof Error ? err.message : 'Please try again')
    } finally { setLoading(false) }
  }, [customerId, showError])

  useEffect(() => { void load() }, [load])

  const resetForm = () => {
    setName(''); setCpm(''); setCurrency('USD'); setChannelId(''); setFrom(''); setNotes('')
  }

  const create = async () => {
    const n = Number(cpm)
    if (!name.trim()) return showError('Name required', 'Give the card a name you will recognise.')
    if (!Number.isFinite(n) || n <= 0) {
      return showError('Check the CPM', 'A card at zero would price a whole season at nothing.')
    }
    if (!from) return showError('Start date required', 'A card has to start somewhere.')

    setBusy(true)
    try {
      await broadcastAdminService.createRateCard(customerId, {
        name: name.trim(),
        cpm: n,
        currency,
        channelId: channelId || null,
        effectiveFrom: from,
        notes: notes.trim() || null
      })
      await load()
      showSuccess('Rate card created', `${money(n, currency)} CPM — assign it to a broadcast to use it.`)
      resetForm(); setAdding(false)
    } catch (err) {
      showError('Could not create the card', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  const close = async () => {
    if (!closing || !closeDate) return
    setBusy(true)
    try {
      await broadcastAdminService.closeRateCard(customerId, closing.id, closeDate)
      await load()
      showSuccess('End date recorded', 'Nothing was repriced — this only records when the rate applied.')
      setClosing(null); setCloseDate('')
    } catch (err) {
      showError('Could not record it', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  const toggle = async (c: RateCard) => {
    setBusy(true)
    try {
      await broadcastAdminService.updateRateCard(customerId, c.id, { isEnabled: !c.is_enabled })
      await load()
      showSuccess(c.is_enabled ? 'No longer offered' : 'Offered again',
        c.is_enabled
          ? `"${c.name}" is out of the picker. Broadcasts using it keep their value.`
          : `"${c.name}" is back in the picker.`)
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  const remove = async (c: RateCard) => {
    setBusy(true)
    try {
      await broadcastAdminService.deleteRateCard(customerId, c.id)
      await load()
      showSuccess('Deleted', `"${c.name}" removed.`)
    } catch (err) {
      showError('Could not delete', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }

  const offered = cards.filter((c) => c.is_enabled)
  const hidden = cards.filter((c) => !c.is_enabled)

  const row = (c: RateCard) => (
    <tr key={c.id} className={`border-t border-gray-100 ${c.is_enabled ? '' : 'bg-gray-50/50'}`}>
      <td className="px-4 py-2.5">
        <p className={`font-medium ${c.is_enabled ? 'text-gray-900' : 'text-gray-500'}`}>{c.name}</p>
        {c.notes && <p className="text-[11px] text-gray-400 mt-0.5 truncate max-w-xs"
                       title={c.notes}>{c.notes}</p>}
      </td>
      <td className="px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
          {c.channel_id
            ? <><Radio className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.75} />
                {c.channel_name}
                {c.channel_territory && <span className="text-gray-400">· {c.channel_territory}</span>}</>
            : <><Globe className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.75} />
                <span className="text-gray-400">Any feed provider</span></>}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
        {money(c.cpm, c.currency)}
      </td>
      <td className="px-4 py-2.5 text-xs whitespace-nowrap text-gray-600">
        {day(c.effective_from)} — {c.effective_to
          ? day(c.effective_to)
          : <span className="text-emerald-700 font-medium">present</span>}
      </td>
      <td className="px-4 py-2.5 text-right text-xs tabular-nums text-gray-600">
        {c.broadcasts_using || '—'}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center justify-end gap-1">
          <Tooltip
            placement="left"
            maxWidth={250}
            content={c.is_enabled
              ? 'Stop offering this card in the picker. Broadcasts already assigned to it keep their value.'
              : 'Offer this card in the picker again.'}
          >
            <button onClick={() => void toggle(c)} disabled={busy}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40">
              {c.is_enabled ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </Tooltip>
          {c.is_current && (
            <Tooltip content="Record when this rate stopped applying. Hides nothing and reprices nothing." placement="left" maxWidth={250}>
              <button onClick={() => { setClosing(c); setCloseDate('') }} disabled={busy}
                className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-40">
                <CalendarX className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
          <Tooltip
            placement="left"
            maxWidth={250}
            content={c.broadcasts_using > 0
              ? `${c.broadcasts_using} broadcast${c.broadcasts_using === 1 ? ' is' : 's are'} priced by this card. Stop offering it instead — they keep their value.`
              : 'Delete this card. Nothing is assigned to it.'}
          >
            <span>
              <button onClick={() => void remove(c)} disabled={busy || c.broadcasts_using > 0}
                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:hover:text-gray-500 disabled:hover:bg-transparent disabled:cursor-not-allowed">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          </Tooltip>
        </div>
      </td>
    </tr>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-gray-500 max-w-2xl leading-snug">
          Named CPMs you assign to a broadcast on its Valuation inputs. Nothing is applied
          automatically — a broadcast is worth nothing until someone picks a card for it, which
          means adding or retiring a card here can never change a figure already sent to a
          sponsor. <span className="font-medium text-gray-600">Rates are never edited.</span> To
          reprice, create a new card and assign it.
        </p>
        {!adding && (
          <button onClick={() => setAdding(true)}
            className="inline-flex items-center flex-shrink-0 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-sm">
            <Plus className="h-3.5 w-3.5 mr-1.5" />New card
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">New rate card</h3>
            <button onClick={() => { resetForm(); setAdding(false) }}
              className="p-1 text-gray-400 hover:text-gray-700 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <label className={label} htmlFor="rc-name">Name</label>
              <input id="rc-name" className={field} value={name} placeholder="ABL 2026-27 national"
                     onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="rc-cpm">CPM</label>
              <div className="flex gap-2">
                <input id="rc-cpm" className={field} inputMode="decimal" value={cpm}
                       placeholder="18.50" onChange={(e) => setCpm(e.target.value)} />
                <select className={`${field} w-24`} value={currency}
                        onChange={(e) => setCurrency(e.target.value)}>
                  {['USD', 'AUD', 'NZD', 'GBP', 'EUR', 'CAD'].map((c) =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={label} htmlFor="rc-channel">Feed Provider</label>
              <select id="rc-channel" className={field} value={channelId}
                      onChange={(e) => setChannelId(e.target.value)}>
                <option value="">Any feed provider</option>
                {[...channels]
                  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.territory ? ` · ${c.territory}` : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="rc-from">Rate applies from</label>
              <input id="rc-from" type="date" className={field} value={from}
                     onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="rc-notes">Notes</label>
              <input id="rc-notes" className={field} value={notes}
                     placeholder="Where this rate came from"
                     onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2.5 leading-snug">
            The feed provider and the date describe the rate; neither restricts where the card can be
            assigned. They are what tells two similarly-named cards apart in the picker, so a
            replay rate and a live rate stay distinguishable.
          </p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => void create()} disabled={busy}
              className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 shadow-sm">
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Creating…</>
                    : <><DollarSign className="h-3.5 w-3.5 mr-1.5" />Create card</>}
            </button>
            <button onClick={() => { resetForm(); setAdding(false) }} disabled={busy}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {closing && (
        <div className="bg-amber-50/60 rounded-lg border border-amber-200 p-4">
          <h3 className="text-sm font-semibold text-amber-900">When did “{closing.name}” stop applying?</h3>
          <p className="text-xs text-amber-800 mt-1 leading-snug max-w-2xl">
            This records a fact about the rate. It does not reprice anything, and it does not
            remove the card from the picker — a game that aired while this rate was in force
            should still be assigned to it. To stop offering the card, use the hide control
            instead.
          </p>
          <div className="flex items-end gap-2 mt-3">
            <div>
              <label className={label} htmlFor="rc-close">Stopped applying on</label>
              <input id="rc-close" type="date" className={field} value={closeDate}
                     min={String(closing.effective_from).slice(0, 10)}
                     onChange={(e) => setCloseDate(e.target.value)} />
            </div>
            <button onClick={() => void close()} disabled={busy || !closeDate}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
              Record
            </button>
            <button onClick={() => { setClosing(null); setCloseDate('') }} disabled={busy}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
          <DollarSign className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">No rate cards</p>
          <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
            Until there is one, a broadcast can only be priced by typing a CPM into it by hand,
            and reads as not priced until someone does. One card is usually enough to start.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr className="text-[10px] uppercase tracking-wider">
                <th className="text-left font-semibold px-4 py-2">Card</th>
                <th className="text-left font-semibold px-4 py-2">Feed Provider</th>
                <th className="text-right font-semibold px-4 py-2">CPM</th>
                <th className="text-left font-semibold px-4 py-2">
                  <Tooltip content="When this rate applied. Descriptive — a closed card can still be assigned." placement="top" maxWidth={240}>
                    <span className="cursor-help">Rate applied</span>
                  </Tooltip>
                </th>
                <th className="text-right font-semibold px-4 py-2">
                  <Tooltip content="Broadcasts assigned to this card." placement="top" maxWidth={200}>
                    <span className="cursor-help">Assigned</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {offered.map(row)}
              {hidden.length > 0 && (
                <tr className="bg-gray-100/70">
                  <td colSpan={6} className="px-4 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    Not offered — still pricing the broadcasts assigned to them
                  </td>
                </tr>
              )}
              {hidden.map(row)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BroadcastRateCards
